import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Brain, ThumbsUp, ThumbsDown, MessageSquareText, Copy, CheckCircle2, RefreshCw, AlertTriangle, Gauge } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Fase 5 — AI Insights section (Pro-only).
 *
 * Guarded server-side by `require_feature("ai_insights")` → 402. We render
 * this section only when the user is on effective Pro. Provider status
 * (`/api/ai/status`) tells us if Gemini is configured; when it isn't we
 * show the "Attiva Gemini" empty state — never a raw API error.
 */
const SENTIMENT_LABEL = {
  positive: "Positivo",
  neutral: "Neutrale",
  negative: "Negativo",
  mixed: "Misto",
};

const SENTIMENT_COLOR = {
  positive: "#4A8A6E",
  neutral: "#E9A94E",
  negative: "#C6423B",
  mixed: "#00F2F4",
};

export default function AiInsightsSection({ businessId, dateRange, presetKey }) {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [quota, setQuota] = useState(null);
  const [sentiment, setSentiment] = useState(null);
  const [strengths, setStrengths] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replyResult, setReplyResult] = useState(null);
  const [replyLoading, setReplyLoading] = useState(false);
  const [replyCopied, setReplyCopied] = useState(false);
  const [tone, setTone] = useState("professionale");

  const refreshQuota = useCallback(async () => {
    try {
      const { data } = await api.get("/ai/quota");
      setQuota(data);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [s, q] = await Promise.all([
          api.get("/ai/status"),
          api.get("/ai/quota").catch(() => ({ data: null })),
        ]);
        setStatus(s.data);
        if (q.data) setQuota(q.data);
      } catch {
        setStatus({ available: false, provider: "noop" });
      } finally {
        setStatusLoading(false);
      }
    })();
  }, []);

  const dateParams = useMemo(() => {
    const p = {};
    if (dateRange?.from) p.start = new Date(dateRange.from).toISOString();
    if (dateRange?.to) p.end = new Date(dateRange.to).toISOString();
    return p;
  }, [dateRange]);

  const runInsights = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    setError(null);
    try {
      const payload = { business_id: businessId, ...dateParams, limit: 50 };
      const [s1, s2] = await Promise.all([
        api.post("/ai/insights/sentiment", payload),
        api.post("/ai/insights/strengths", payload),
      ]);
      setSentiment(s1.data);
      setStrengths(s2.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429 && detail?.code === "ai_quota_exceeded") {
        setError(detail.message || `Hai raggiunto il limite mensile di ${detail.limit} utilizzi AI.`);
      } else if (status === 503) {
        setError("Il servizio AI non è raggiungibile al momento. Riprova più tardi.");
      } else if (status === 402) {
        setError("Serve un piano Pro attivo per usare AI Insights.");
      } else if (status === 400) {
        setError("Non ci sono abbastanza recensioni nel periodo selezionato per generare gli insight.");
      } else {
        setError("Impossibile generare gli insight in questo momento.");
      }
    } finally {
      setLoading(false);
      refreshQuota();
    }
  }, [businessId, dateParams, refreshQuota]);

  const generateReply = useCallback(async () => {
    if (!replyText.trim()) {
      toast.error("Incolla il testo della recensione");
      return;
    }
    setReplyLoading(true);
    setReplyResult(null);
    try {
      const { data } = await api.post("/ai/reply", {
        text: replyText,
        tone,
      });
      setReplyResult(data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429 && detail?.code === "ai_quota_exceeded") {
        toast.error(detail.message || "Quota AI mensile esaurita.");
      } else if (status === 503) {
        toast.error("Servizio AI momentaneamente non disponibile.");
      } else if (status === 402) {
        toast.error("Serve un piano Pro attivo.");
      } else {
        toast.error("Impossibile generare la risposta.");
      }
    } finally {
      setReplyLoading(false);
      refreshQuota();
    }
  }, [replyText, tone, refreshQuota]);

  const copyReply = async () => {
    if (!replyResult?.reply) return;
    try {
      await navigator.clipboard.writeText(replyResult.reply);
      setReplyCopied(true);
      toast.success("Risposta copiata");
      setTimeout(() => setReplyCopied(false), 1800);
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  if (statusLoading) return null;

  // Empty state: Gemini not configured — never surface raw API errors
  if (!status?.available) {
    return (
      <section
        data-testid="ai-insights-section"
        data-provider={status?.provider || "noop"}
        className="rounded-2xl border border-[var(--border-c)] bg-[var(--bg-2)] p-6 md:p-8"
      >
        <div className="flex items-center gap-2 text-[10px] font-sans uppercase tracking-[0.25em] text-[#00F2F4] font-semibold">
          <Brain size={12} strokeWidth={2.2} /> AI Insights
        </div>
        <h3 className="mt-3 font-serif text-2xl md:text-3xl text-[var(--fg-1)] tracking-tight">
          Attiva Gemini per utilizzare AI Insights.
        </h3>
        <p className="mt-2 text-sm text-[var(--fg-3)] max-w-xl">
          Analisi del sentiment, punti di forza e generazione risposte in un click. Configura il provider AI per iniziare.
        </p>
      </section>
    );
  }

  return (
    <motion.section
      initial={{ y: 12, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      data-testid="ai-insights-section"
      data-provider={status.provider}
      className="rounded-2xl border border-[var(--border-c)] bg-[var(--bg-2)] p-6 md:p-8 shadow-[0_2px_20px_rgba(0,0,0,0.15)]"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#00F2F4] font-sans font-semibold flex items-center gap-2">
            <Brain size={12} strokeWidth={2.2} /> AI Insights
            {quota && quota.limit > 0 && (
              <QuotaChip quota={quota} />
            )}
            {quota && quota.unlimited && (
              <span data-testid="ai-quota-chip" className="ml-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-sans font-semibold bg-[#00F2F4]/15 text-[#00F2F4]">
                <Gauge size={10} /> Illimitati
              </span>
            )}
          </div>
          <h3 className="mt-2 font-serif text-2xl md:text-3xl text-[var(--fg-1)] tracking-tight">
            Cosa dicono davvero i tuoi clienti
          </h3>
          <p className="mt-1 text-sm text-[var(--fg-3)] max-w-xl">
            Sentiment, punti di forza e criticità estratti dalle recensioni del periodo selezionato.
          </p>
        </div>
        <button
          data-testid="ai-insights-run-btn"
          onClick={runInsights}
          disabled={loading || !businessId || (quota && !quota.unlimited && quota.remaining === 0)}
          className="inline-flex items-center gap-2 rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] disabled:opacity-60 disabled:cursor-not-allowed text-[#0A0A0A] px-5 py-2.5 text-sm font-sans font-semibold"
          style={{ transition: "background-color 0.2s ease" }}
        >
          {loading ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} strokeWidth={2.2} />}
          {loading ? "Analisi in corso…" : (sentiment ? "Rigenera insight" : "Genera insight")}
        </button>
      </div>

      {quota && !quota.unlimited && quota.remaining === 0 && (
        <div
          data-testid="ai-quota-banner"
          className="mt-4 rounded-xl border border-[#E9A94E]/40 bg-[#E9A94E]/10 p-4 text-sm text-[var(--fg-2)] flex items-start gap-2"
        >
          <AlertTriangle size={16} className="text-[#E9A94E] mt-0.5" />
          <span>
            Hai usato tutti gli <strong className="text-[var(--fg-1)]">{quota.limit}</strong> utilizzi AI di questo mese. Si azzererà il {formatResetsAt(quota.resets_at)}.
          </span>
        </div>
      )}

      {error && (
        <div
          data-testid="ai-insights-error"
          className="mt-6 rounded-xl border border-[#C6423B]/40 bg-[#C6423B]/10 p-4 text-sm text-[var(--fg-2)] flex items-start gap-2"
        >
          <AlertTriangle size={16} className="text-[#C6423B] mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {(sentiment || strengths) && !error && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Sentiment card */}
          {sentiment && (
            <div data-testid="ai-sentiment-card" className="rounded-xl border border-[var(--border-c)] bg-[var(--bg-3)] p-5">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] font-sans">Sentiment</div>
                <span
                  data-testid="ai-sentiment-overall"
                  className="text-[10px] uppercase tracking-[0.2em] font-sans font-semibold px-2 py-0.5 rounded-full"
                  style={{
                    color: SENTIMENT_COLOR[sentiment.overall] || "#00F2F4",
                    borderColor: (SENTIMENT_COLOR[sentiment.overall] || "#00F2F4") + "55",
                    border: "1px solid",
                  }}
                >
                  {SENTIMENT_LABEL[sentiment.overall] || sentiment.overall}
                </span>
              </div>
              <div className="mt-3 font-serif text-3xl text-[var(--fg-1)] leading-none" data-testid="ai-sentiment-score">
                {Number(sentiment.score || 0).toFixed(2)}
              </div>
              <p className="mt-2 text-sm text-[var(--fg-2)] leading-relaxed" data-testid="ai-sentiment-summary">
                {sentiment.summary}
              </p>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <SentimentPill label="Positive" value={sentiment.breakdown?.positive || 0} color="#4A8A6E" />
                <SentimentPill label="Neutre" value={sentiment.breakdown?.neutral || 0} color="#E9A94E" />
                <SentimentPill label="Negative" value={sentiment.breakdown?.negative || 0} color="#C6423B" />
              </div>
              <div className="mt-2 text-[10px] text-[var(--fg-3)]">Su {sentiment.sample_size} recensioni</div>
            </div>
          )}

          {/* Strengths / Weaknesses card */}
          {strengths && (
            <div data-testid="ai-strengths-card" className="rounded-xl border border-[var(--border-c)] bg-[var(--bg-3)] p-5 space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] font-sans flex items-center gap-1.5">
                  <ThumbsUp size={11} className="text-[#4A8A6E]" /> Punti di forza
                </div>
                <ul className="mt-2 space-y-1.5" data-testid="ai-strengths-list">
                  {(strengths.strengths || []).slice(0, 5).map((s, i) => (
                    <li key={`s-${i}`} className="text-sm text-[var(--fg-2)] leading-relaxed">
                      <span className="text-[var(--fg-1)] font-semibold">{s.title}.</span> {s.description}
                    </li>
                  ))}
                  {(strengths.strengths || []).length === 0 && (
                    <li className="text-xs text-[var(--fg-3)]">Nessun punto di forza rilevato.</li>
                  )}
                </ul>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] font-sans flex items-center gap-1.5">
                  <ThumbsDown size={11} className="text-[#C6423B]" /> Criticità
                </div>
                <ul className="mt-2 space-y-1.5" data-testid="ai-weaknesses-list">
                  {(strengths.weaknesses || []).slice(0, 5).map((s, i) => (
                    <li key={`w-${i}`} className="text-sm text-[var(--fg-2)] leading-relaxed">
                      <span className="text-[var(--fg-1)] font-semibold">{s.title}.</span> {s.description}
                    </li>
                  ))}
                  {(strengths.weaknesses || []).length === 0 && (
                    <li className="text-xs text-[var(--fg-3)]">Nessuna criticità significativa rilevata.</li>
                  )}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* AI Reply block */}
      <div className="mt-6 rounded-xl border border-[var(--border-c)] bg-[var(--bg-3)] p-5">
        <button
          type="button"
          onClick={() => setReplyOpen((o) => !o)}
          data-testid="ai-reply-toggle"
          className="w-full flex items-center justify-between gap-3 text-left"
        >
          <div className="flex items-center gap-2">
            <MessageSquareText size={14} className="text-[#00F2F4]" />
            <span className="font-sans font-semibold text-sm text-[var(--fg-1)]">Suggerisci una risposta AI</span>
          </div>
          <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] font-sans">
            {replyOpen ? "Chiudi" : "Apri"}
          </span>
        </button>

        {replyOpen && (
          <div className="mt-4 space-y-3">
            <textarea
              data-testid="ai-reply-input"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Incolla qui il testo della recensione a cui vuoi rispondere…"
              rows={4}
              className="w-full rounded-lg border border-[var(--border-c)] bg-[var(--bg-2)] p-3 text-sm text-[var(--fg-1)] font-sans placeholder:text-[var(--fg-3)] focus:border-[#00F2F4] outline-none"
              style={{ transition: "border-color 0.2s ease" }}
            />
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 text-xs text-[var(--fg-3)] font-sans">
                <span className="uppercase tracking-[0.2em] text-[10px]">Tono</span>
                <select
                  data-testid="ai-reply-tone"
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="bg-[var(--bg-2)] border border-[var(--border-c)] rounded-full px-3 py-1.5 text-xs text-[var(--fg-1)]"
                >
                  <option value="professionale">Professionale</option>
                  <option value="amichevole">Amichevole</option>
                  <option value="empatico">Empatico</option>
                  <option value="scusa">Di scuse</option>
                </select>
              </div>
              <button
                onClick={generateReply}
                disabled={replyLoading}
                data-testid="ai-reply-generate-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] disabled:opacity-60 text-[#0A0A0A] px-4 py-2 text-xs font-sans font-semibold"
                style={{ transition: "background-color 0.2s ease" }}
              >
                {replyLoading ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {replyLoading ? "Generazione…" : "Genera risposta"}
              </button>
              <span className="text-[10px] text-[var(--fg-3)] italic">
                Suggerimento — non viene pubblicato automaticamente su Google.
              </span>
            </div>

            {replyResult && (
              <div data-testid="ai-reply-result" className="rounded-lg border border-[#00F2F4]/30 bg-[var(--bg-2)] p-4">
                <div className="flex items-center justify-between gap-2 text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] font-sans">
                  <span>Risposta suggerita</span>
                  <span className="flex items-center gap-2">
                    <span data-testid="ai-reply-language">Lingua: {replyResult.language}</span>
                    <span>·</span>
                    <span data-testid="ai-reply-tone-out">Tono: {replyResult.tone}</span>
                  </span>
                </div>
                <p className="mt-2 text-sm text-[var(--fg-1)] leading-relaxed whitespace-pre-wrap" data-testid="ai-reply-text">
                  {replyResult.reply}
                </p>
                <button
                  onClick={copyReply}
                  data-testid="ai-reply-copy-btn"
                  className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[var(--border-c)] hover:border-[#00F2F4] text-[var(--fg-1)] px-3 py-1.5 text-xs font-sans"
                  style={{ transition: "border-color 0.2s ease" }}
                >
                  {replyCopied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
                  {replyCopied ? "Copiato" : "Copia risposta"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.section>
  );
}

function SentimentPill({ label, value, color }) {
  return (
    <div className="rounded-lg border border-[var(--border-c)] bg-[var(--bg-2)] px-2 py-2">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] font-sans">{label}</div>
      <div className="mt-1 font-serif text-xl leading-none" style={{ color }}>{value}</div>
    </div>
  );
}

function QuotaChip({ quota }) {
  const percent = quota.percent || 0;
  let color = "#00F2F4";
  if (percent >= 90) color = "#C6423B";
  else if (percent >= 70) color = "#E9A94E";
  return (
    <span
      data-testid="ai-quota-chip"
      data-percent={percent}
      className="ml-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-sans font-semibold"
      style={{
        color,
        border: `1px solid ${color}55`,
        backgroundColor: `${color}15`,
      }}
      title={`Utilizzi AI questo mese`}
    >
      <Gauge size={10} strokeWidth={2.4} />
      {quota.used}/{quota.limit}
    </span>
  );
}

function formatResetsAt(iso) {
  if (!iso) return "il 1° del mese prossimo";
  try {
    const d = new Date(iso);
    return `${d.getDate()} ${d.toLocaleString("it-IT", { month: "long" })}`;
  } catch {
    return "il 1° del mese prossimo";
  }
}
